// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const ANNOUNCEMENTS = [
  {
    id: 1, title: "Sprint 13 Kickoff - Q1 Push Begins", content: "Team, we are officially in Sprint 13 and this is our biggest push of Q1. Key priorities:\n\n- Finalize the Canstorr and Apex Insurance deals\n- Complete all beta testing for VitaSync\n- Ship the real-time notifications system\n- Board presentation ready by Friday\n\nLet's stay focused and keep the momentum going. Daily standups at 9 AM sharp.",
    author: "Ryan Miskulin", date: "2026-02-10", priority: "High", category: "Company News", pinned: true, read: true
  },
  {
    id: 2, title: "HealthFirst Clinics - Closed Won!", content: "Huge congratulations to Des Bachynski for closing our first VitaSync enterprise deal! HealthFirst Clinics has signed a 2-year contract worth $48,000. This is a major milestone for the health vertical.\n\nDes did an outstanding job managing the beta testing process and building trust with their team. The onboarding is already underway.\n\nThis proves our health platform is ready for scale. Let's keep pushing!",
    author: "Ryan Miskulin", date: "2026-02-08", priority: "Normal", category: "Achievement", pinned: true, read: true
  },
  {
    id: 3, title: "New Deployment Process - Effective Immediately", content: "Starting today, all production deployments must go through the updated CI/CD pipeline. Key changes:\n\n- All merges to main require 1 code review approval\n- Automated test suite must pass before deploy\n- Staging environment validation is now mandatory\n- Use the command: npx wrangler pages deploy build --project-name=[name] --branch=main\n\nPlease update your local environments accordingly. Reach out to Jordan if you have questions.",
    author: "Jordan Lutz", date: "2026-02-07", priority: "High", category: "Product Update", pinned: false, read: true
  },
  {
    id: 4, title: "Q1 Benefits Enrollment Window Open", content: "The Q1 benefits enrollment window is now open through February 28. Please review the updated plan options and submit your selections through the HR portal.\n\nKey updates this quarter:\n- Dental coverage now includes orthodontics\n- Vision plan expanded to include LASIK partial coverage\n- New wellness stipend of $500/year\n\nContact Clorice Robertson with any questions about your benefits.",
    author: "Clorice Robertson", date: "2026-02-05", priority: "Normal", category: "HR/Policy", pinned: false, read: false
  },
  {
    id: 5, title: "Company All-Hands Meeting - February 21", content: "Mark your calendars for our monthly all-hands meeting on February 21 at 10 AM CST. Agenda includes:\n\n- Q1 progress review and financial update from Taylor\n- Product roadmap highlights from Jordan\n- Sales pipeline deep dive from Lucas\n- Open Q&A session\n\nZoom link will be sent out the day before. Attendance is mandatory.",
    author: "Ryan Miskulin", date: "2026-02-04", priority: "Normal", category: "Event", pinned: false, read: false
  },
  {
    id: 6, title: "Canstorr Partnership Progress Update", content: "Quick update on the Canstorr Commodities partnership. We are in advanced negotiations with their team and expect to finalize terms by mid-February.\n\nTyson has been doing excellent work on the ground in Wroxton. The partnership will expand our agricultural footprint significantly across Western Canada.\n\nStay tuned for the official announcement.",
    author: "Tyson Becker", date: "2026-02-03", priority: "Normal", category: "Company News", pinned: false, read: true
  },
];

const CATEGORIES = ["All", "Company News", "Product Update", "HR/Policy", "Event", "Achievement"];
const PRIORITIES_OPTS = ["Normal", "High", "Urgent"];

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState(ANNOUNCEMENTS);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", content: "", priority: "Normal", category: "Company News" });
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);

  const filtered = announcements.filter(a => {
    if (categoryFilter !== "All" && a.category !== categoryFilter) return false;
    return true;
  });

  const pinned = filtered.filter(a => a.pinned);
  const regular = filtered.filter(a => !a.pinned);
  const unreadCount = announcements.filter(a => !a.read).length;

  const categoryColor = (cat) => {
    const colors = { "Company News": "#3b82f6", "Product Update": "#10b981", "HR/Policy": "#8b5cf6", Event: "#eab308", Achievement: "#ec4899" };
    return colors[cat] || "#64748b";
  };

  const priorityBadge = (priority) => {
    if (priority === "Urgent") return <span className="badge badge-red">Urgent</span>;
    if (priority === "High") return <span className="badge badge-yellow">High Priority</span>;
    return null;
  };

  const toggleRead = (id) => {
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
  };

  const createAnnouncement = () => {
    if (!newAnnouncement.title || !newAnnouncement.content) return;
    setAnnouncements(prev => [{
      ...newAnnouncement,
      id: Date.now(),
      author: "You",
      date: "2026-02-09",
      pinned: false,
      read: true,
    }, ...prev]);
    setNewAnnouncement({ title: "", content: "", priority: "Normal", category: "Company News" });
    setShowCreateModal(false);
  };

  const renderAnnouncement = (announcement) => {
    const isExpanded = expandedAnnouncement === announcement.id;
    return (
      <div key={announcement.id} className="card" style={{ marginTop: 12, cursor: "pointer", borderLeft: !announcement.read ? "3px solid #3b82f6" : undefined, borderColor: isExpanded ? "#10b981" : undefined }}
        onClick={() => { setExpandedAnnouncement(isExpanded ? null : announcement.id); toggleRead(announcement.id); }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              {announcement.pinned && <span style={{ fontSize: 11, fontWeight: 700, color: "#eab308", background: "#eab30822", padding: "2px 8px", borderRadius: 4 }}>PINNED</span>}
              <span className="badge" style={{ background: categoryColor(announcement.category) + "22", color: categoryColor(announcement.category) }}>{announcement.category}</span>
              {priorityBadge(announcement.priority)}
              {!announcement.read && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", display: "inline-block" }} />}
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0", margin: "6px 0" }}>{announcement.title}</h3>
            <div style={{ display: "flex", gap: 12, fontSize: 12, color: "#64748b" }}>
              <span>By <strong style={{ color: "#10b981" }}>{announcement.author}</strong></span>
              <span>{announcement.date}</span>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #334155" }}>
            <div style={{ fontSize: 14, color: "#e2e8f0", lineHeight: 1.8, whiteSpace: "pre-line" }}>
              {announcement.content.split("\n").map((line, i) => {
                if (line.startsWith("- ")) {
                  return <div key={i} style={{ paddingLeft: 16, position: "relative" }}><span style={{ position: "absolute", left: 0, color: "#10b981" }}>*</span>{line.substring(2)}</div>;
                }
                return <div key={i}>{line || <br />}</div>;
              })}
            </div>
          </div>
        )}

        {!isExpanded && (
          <div style={{ marginTop: 8, fontSize: 13, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {announcement.content.split("\n")[0]}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div><h1>Announcements</h1><p>{announcements.length} announcements{unreadCount > 0 && <span style={{ color: "#3b82f6" }}> ({unreadCount} unread)</span>}</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ New Announcement</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total</div><div className="stat-value">{announcements.length}</div></div>
        <div className="stat-card"><div className="stat-label">Unread</div><div className="stat-value" style={{ color: "#3b82f6" }}>{unreadCount}</div></div>
        <div className="stat-card"><div className="stat-label">Pinned</div><div className="stat-value" style={{ color: "#eab308" }}>{announcements.filter(a => a.pinned).length}</div></div>
        <div className="stat-card"><div className="stat-label">This Week</div><div className="stat-value">{announcements.filter(a => a.date >= "2026-02-03").length}</div></div>
      </div>

      {/* Category Filters */}
      <div className="card" style={{ display: "flex", gap: 8, padding: "12px 20px", flexWrap: "wrap" }}>
        {CATEGORIES.map(c => (
          <button key={c} className={"btn btn-sm " + (categoryFilter === c ? "btn-primary" : "btn-outline")} onClick={() => setCategoryFilter(c)}>
            {c}
            {c !== "All" && <span style={{ marginLeft: 4, opacity: 0.7 }}>({announcements.filter(a => a.category === c).length})</span>}
          </button>
        ))}
      </div>

      {/* Pinned Announcements */}
      {pinned.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#eab308", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Pinned Announcements</div>
          {pinned.map(renderAnnouncement)}
        </div>
      )}

      {/* Regular Announcements */}
      {regular.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Recent Announcements</div>
          {regular.map(renderAnnouncement)}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="card" style={{ marginTop: 16, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 15, color: "#64748b" }}>No announcements match this filter.</div>
        </div>
      )}

      {/* Create Announcement Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Announcement</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Title</label>
                <input className="input" value={newAnnouncement.title} onChange={e => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} placeholder="Announcement title" />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Category</label>
                  <select value={newAnnouncement.category} onChange={e => setNewAnnouncement({ ...newAnnouncement, category: e.target.value })} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", width: "100%" }}>
                    {CATEGORIES.slice(1).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Priority</label>
                  <select value={newAnnouncement.priority} onChange={e => setNewAnnouncement({ ...newAnnouncement, priority: e.target.value })} style={{ padding: "8px 12px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", width: "100%" }}>
                    {PRIORITIES_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label>Content</label>
                <textarea value={newAnnouncement.content} onChange={e => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })} placeholder="Write your announcement... Use - for bullet points." style={{ minHeight: 150, padding: "10px 14px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0", fontSize: 13, lineHeight: 1.6, resize: "vertical", width: "100%", fontFamily: "inherit" }} />
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Tip: Use "- " at the start of a line for bullet points. Leave blank lines for paragraphs.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createAnnouncement}>Publish Announcement</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
